import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ChatResponseNotification.css';

interface ChatResponseNotificationProps {
  message: string;
  responsePreview: string;
  onDismiss: () => void;
  onNavigateToChat: () => void;
}

/**
 * 🔔 Chat Response Notification Component
 * 
 * Shows an in-app notification when a chat response completes while the user
 * is on a different screen. Mimics iOS/Android notification style.
 * 
 * Features:
 * - iOS/Android-style notification design
 * - Tap to navigate to chat
 * - Swipe to dismiss
 * - Auto-dismiss after 8 seconds
 * - Coach Cedar branding
 */
export const ChatResponseNotification: React.FC<ChatResponseNotificationProps> = ({
  message,
  responsePreview,
  onDismiss,
  onNavigateToChat
}) => {
  const navigate = useNavigate();

  const handleTap = () => {
    onNavigateToChat();
    navigate('/chat');
  };

  // L8 FIX (Mission 35B): Track touchend listener so we can clean up on unmount
  const touchEndListenerRef = useRef<((e: TouchEvent) => void) | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup any dangling touchend listener on unmount
      if (touchEndListenerRef.current) {
        document.removeEventListener('touchend', touchEndListenerRef.current);
      }
    };
  }, []);

  const handleSwipe = (e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    const startX = touch.clientX;

    // Remove previous listener if any
    if (touchEndListenerRef.current) {
      document.removeEventListener('touchend', touchEndListenerRef.current);
    }

    const handleTouchEnd = (endEvent: TouchEvent) => {
      const endTouch = endEvent.changedTouches[0];
      const deltaX = endTouch.clientX - startX;

      if (deltaX > 100) {
        onDismiss();
      }

      document.removeEventListener('touchend', handleTouchEnd);
      touchEndListenerRef.current = null;
    };

    touchEndListenerRef.current = handleTouchEnd;
    document.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <div 
      className="chat-response-notification"
      onClick={handleTap}
      onTouchStart={handleSwipe}
    >
      <div className="notification-header">
        <div className="notification-icon">
          <div className="cedar-avatar">CC</div>
        </div>
        <div className="notification-content">
          <div className="notification-title">Coach Cedar</div>
          <div className="notification-subtitle">now</div>
        </div>
        <button 
          className="notification-dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          ×
        </button>
      </div>
      <div className="notification-body">
        <div className="notification-message">{message}</div>
        <div className="notification-preview">{responsePreview}</div>
      </div>
      <div className="notification-actions">
        <span className="notification-action-hint">Tap to view response</span>
      </div>
    </div>
  );
};

export default ChatResponseNotification;
